<script setup>
   import { ref, computed } from 'vue';
   import { storeToRefs } from 'pinia';
   import LmsNavList from './LmsNavList.vue';
   import UserMenu from './UserMenu.vue';
   import getViteEnvVar from '../functions/getViteEnvVar.ts';
   import LoginButton from './LoginButton.vue';
   import { useAuth0 } from '@auth0/auth0-vue';
   import { useRuntimeConfig } from '#imports';
   import useAuthStore from '../stores/auth';
   import { LmsRouteNames } from '../types/types';
   import { usePortalBranding } from '../composables/usePortalBranding';
   import { useScrollNav } from '../composables/useScrollNav';
   import { useNativeBackNavigation } from '../composables/useNativeBackNavigation';

   //store
   const authStore = useAuthStore();
   const { user } = storeToRefs(authStore);

   //Auth0
   const { isAuthenticated, isLoading } = useAuth0();

   //Portal branding
   const config = useRuntimeConfig();
   const brandLogo = config.public.brandLogo;
   const { portalLogo, portalColorScheme } = usePortalBranding();

   //Scroll nav
   const { isVisible, isScrolled } = useScrollNav();

   //Back navigation — packaged app only, where nothing else provides it
   const { canGoBack, canGoForward, goBack, goForward } = useNativeBackNavigation();

   const navListRef = ref(null);

   const isIpaPortal = computed(() => {
      if (brandLogo) {
         return false;
      }
      const portal = authStore?.user?.site_settings?.portal;
      return portal === 'ipa' || portal === 'ipapolo' || portal === 'lms1';
   });

   const mainNavLogo = computed(() => {
      if (brandLogo) {
         return brandLogo;
      }

      const logoFilename = authStore?.user?.site_settings?.logo_filename;

      if (isIpaPortal.value) {
         return '/img/brand_logos/ipa-polo-top.webp';
      }

      if (!logoFilename) {
         return '';
      }

      return portalLogo.value;
   });

   const leftNavLogo = computed(() => {
      if (brandLogo) {
         return brandLogo;
      }

      const logoFilename = authStore?.user?.site_settings?.logo_filename;

      if (isIpaPortal.value) {
         return '/img/brand_logos/ipa-polo-left.webp';
      }

      if (!logoFilename) {
         return '';
      }

      return portalLogo.value;
   });

   const showHomePageLink = computed(() => {
      const navItems = authStore?.user?.site_settings?.nav_items;
      if (!navItems) {
         return true;
      }
      const homeItem = navItems.find(item => item.key === 'home');
      return homeItem?.is_visible ?? true;
   });

   // UserMenu: always show for authenticated users so everyone can log out.
   // Profile items inside UserMenu are controlled by useUserProfileMenu (portal/feature checks).
   const showUserMenu = computed(() => {
      return !!authStore?.user?.site_settings?.portal;
   });

   function triggerMenuOpen() {
      if (navListRef.value && typeof navListRef.value.openMenu === 'function') {
         navListRef.value.openMenu();
      }
   }
</script>

<template>
   <div
      data-nav-main
      class="fixed top-0 z-50 flex w-full flex-col items-center justify-center bg-white pt-[env(safe-area-inset-top)] transition-all duration-300 lg:px-3"
      :class="[
         portalColorScheme.cssClass,
         isVisible
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none -translate-y-2 opacity-0',
      ]"
      style="box-shadow: 1px 1px 16px 0 rgba(0, 0, 0, 0.24)">
      <div class="w-full max-w-lmsCenteredContainer">
         <!-- Top centered branding - desktop only, hidden when scrolled, IPA portals only -->
         <div
            v-if="isIpaPortal"
            class="hidden py-4 transition-all duration-300 lg:flex lg:items-center lg:justify-center"
            :class="[
               isScrolled
                  ? 'lg:max-h-0 lg:overflow-hidden lg:py-0 lg:opacity-0'
                  : 'lg:max-h-20 lg:opacity-100',
            ]">
            <NuxtLink
               v-if="showHomePageLink"
               :to="{
                  name: LmsRouteNames.home,
               }">
               <TextLogo variant="centered" />
            </NuxtLink>
            <TextLogo v-else variant="centered" />
         </div>

         <div class="mx-auto flex w-full items-center justify-between">
            <!-- Back control, packaged app only. Top-left is where an iOS user reaches for
                 it, so it sits ahead of the branding rather than beside the hamburger.
                 `canGoBack` is false on the web, so this never renders there. -->
            <button
               v-if="canGoBack"
               type="button"
               data-testid="app-back-button"
               aria-label="Go back"
               class="-mr-2 p-3 text-gray-700"
               @click="goBack">
               <i class="pi pi-chevron-left text-xl"></i>
            </button>

            <!-- Forward appears only after going back, and vanishes again as soon as a new
                 route is pushed — so it is absent during ordinary browsing rather than
                 sitting there advertising a browser. -->
            <button
               v-if="canGoForward"
               type="button"
               data-testid="app-forward-button"
               aria-label="Go forward"
               class="-mr-2 p-3 text-gray-700"
               @click="goForward">
               <i class="pi pi-chevron-right text-xl"></i>
            </button>

            <!-- Left branding/logo - IPA portals: text branding with scroll behavior, Non-IPA: logo always visible -->
            <NuxtLink
               v-if="showHomePageLink && isIpaPortal"
               :to="{
                  name: LmsRouteNames.home,
               }"
               class="ml-3 py-3 transition-all duration-300 lg:ml-0"
               :class="[
                  !isScrolled
                     ? 'lg:max-h-0 lg:max-w-0 lg:overflow-hidden lg:py-0 lg:opacity-0'
                     : 'lg:max-w-full lg:py-2 lg:opacity-100',
               ]">
               <TextLogo variant="left" title-size="text-lg" />
            </NuxtLink>
            <div
               v-else-if="!showHomePageLink && isIpaPortal"
               class="ml-3 py-3 transition-all duration-300 lg:ml-0"
               :class="[
                  !isScrolled
                     ? 'lg:max-h-0 lg:max-w-0 lg:overflow-hidden lg:py-0 lg:opacity-0'
                     : 'lg:max-w-full lg:py-2 lg:opacity-100',
               ]">
               <TextLogo variant="left" title-size="text-lg" />
            </div>

            <!-- Non-IPA portals: show logo -->
            <NuxtLink
               v-if="showHomePageLink && !isIpaPortal"
               :to="{
                  name: LmsRouteNames.home,
               }"
               class="ml-3 py-3 transition-all duration-300 lg:ml-0 lg:max-w-20 lg:py-2 lg:opacity-100">
               <img
                  class="w-16 lg:w-20"
                  :src="leftNavLogo"
                  :alt="authStore?.user?.site_settings?.brand_name" />
            </NuxtLink>
            <div
               v-else-if="!showHomePageLink && !isIpaPortal"
               class="ml-3 py-3 transition-all duration-300 lg:ml-0 lg:max-w-20 lg:py-2 lg:opacity-100">
               <img
                  class="w-16 lg:w-20"
                  :src="leftNavLogo"
                  :alt="authStore?.user?.site_settings?.brand_name" />
            </div>

            <a
               href="#app-main-menu"
               class="ml-auto p-3 text-gray-700 lg:hidden"
               @click.prevent="triggerMenuOpen">
               <i class="pi pi-bars text-2xl"></i>
            </a>

            <LmsNavList ref="navListRef" class="lg:flex" />

            <UserMenu
               class="hidden sm:flex"
               v-if="!isLoading && showUserMenu" />
            <LoginButton />
         </div>
      </div>
   </div>
   <hr class="w-full dark:border-gray-700" />
</template>
